import { useLocation } from "react-router-dom";
import { LogOut, Menu as MenuIcon, ShieldCheck, UserRound } from "lucide-react";
import { ROLE_LABELS } from "@ir/types";
import { Avatar, Badge, IconButton, Menu, ThemeToggle, cn } from "@ir/ui";
import { useAuth } from "../auth/AuthProvider";
import { NAV_SECTIONS } from "./navigation";

/** Sticky header: page title on the left, identity and theme on the right. */
export function Topbar({ onOpenNav }: { onOpenNav: () => void }) {
  const { user, signOut } = useAuth();
  const location = useLocation();

  const current = NAV_SECTIONS.flatMap((section) => section.items).find((item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
  );

  return (
    <header className="sticky top-0 z-30 border-b border-line glass">
      <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
        <IconButton
          icon={MenuIcon}
          label="Open navigation"
          onClick={onOpenNav}
          className="lg:hidden"
        />

        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-semibold text-content">
            {current?.label ?? "Marketplace admin"}
          </p>
          {current?.description && (
            <p className="hidden truncate text-xs text-content-subtle sm:block">
              {current.description}
            </p>
          )}
        </div>

        <ThemeToggle />

        {user && (
          <Menu
            label="Account"
            items={[
              {
                id: "identity",
                label: user.email,
                icon: UserRound,
                disabled: true,
              },
              {
                id: "role",
                label: `Role: ${ROLE_LABELS[user.role]}`,
                icon: ShieldCheck,
                disabled: true,
              },
              {
                id: "sign-out",
                label: "Sign out",
                icon: LogOut,
                separated: true,
                danger: true,
                onSelect: () => void signOut(),
              },
            ]}
            trigger={({ open, toggle }) => (
              <button
                type="button"
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={open}
                className={cn(
                  "flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 transition-colors",
                  "hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500",
                  open && "bg-surface-sunken",
                )}
              >
                <Avatar name={user.name} email={user.email} src={user.picture} size={32} />
                <span className="hidden min-w-0 text-left sm:block">
                  <span className="block max-w-36 truncate text-xs font-medium text-content">
                    {user.name ?? user.email}
                  </span>
                  <span className="block text-[11px] text-content-subtle">
                    {ROLE_LABELS[user.role]}
                  </span>
                </span>
              </button>
            )}
          />
        )}
      </div>

      {user?.role === "read_only" && (
        <div className="flex items-center justify-center gap-2 border-t border-line bg-surface-sunken px-4 py-1.5">
          <Badge tone="neutral" dot>
            Read-only access — you can view records but not change them
          </Badge>
        </div>
      )}
    </header>
  );
}
